'use client';

import React, { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import ProtectedRoute from '@/components/ProtectedRoute';
import { Download, Filter } from 'lucide-react';
import { DashboardRHTab } from './components/DashboardRHTab';
import { PerformanceIndicatorsTab } from './components/PerformanceIndicatorsTab';

// Simple Tab Components using React Context (following dashboard pattern)
interface TabsContextType {
  activeTab: string;
  setActiveTab: (value: string) => void;
}

const TabsContext = React.createContext<TabsContextType | null>(null);

function Tabs({ defaultValue, children }: { defaultValue: string, children: React.ReactNode }) {
  const [activeTab, setActiveTab] = useState(defaultValue);
  
  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab }}>
      <div>
        {children}
      </div>
    </TabsContext.Provider>
  );
}

function TabsList({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex bg-brand-dark-light/80 backdrop-blur-sm rounded-monumenta p-1 mb-6 shadow-monumenta border border-gray-800/50">
      {children}
    </div>
  );
}

function TabsTrigger({ value, children }: { value: string, children: React.ReactNode }) {
  const context = React.useContext(TabsContext);
  if (!context) throw new Error('TabsTrigger must be used within Tabs');
  
  const { activeTab, setActiveTab } = context;
  const isActive = activeTab === value;
  
  return (
    <button
      onClick={() => setActiveTab(value)}
      className={`px-6 py-3 rounded-monumenta font-display font-semibold transition-all duration-300 flex-1 ${
        isActive 
          ? 'bg-brand-primary text-brand-dark shadow-monumenta-brand' 
          : 'text-gray-300 hover:text-white hover:bg-brand-dark-lighter transform hover:scale-105'
      }`}
    >
      {children}
    </button>
  );
}

function TabsContent({ value, children }: { value: string, children: React.ReactNode }) {
  const context = React.useContext(TabsContext);
  if (!context) throw new Error('TabsContent must be used within Tabs');
  
  const { activeTab } = context;
  if (activeTab !== value) return null;
  return <div className="animate-fade-in-up">{children}</div>;
}

function PeopleManagementContent() {
  const { user } = useAuth();

  if (!user) return null;

  return (
    
      <div className="space-y-8">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mb-2 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
              Gestão de Pessoas
            </h1>
            <p className="text-brand-gray-light font-body text-lg">
              Dashboard RH e Indicadores de Performance
            </p>
          </div>
          <div className="flex gap-3">
            <button className="btn-secondary flex items-center gap-2 group">
              <Download className="w-4 h-4 transition-transform duration-200 group-hover:scale-110" />
              Exportar
            </button>
            <button className="btn-secondary flex items-center gap-2 group">
              <Filter className="w-4 h-4 transition-transform duration-200 group-hover:rotate-12" />
              Filtros
            </button>
          </div>
        </div>

        <Tabs defaultValue="dashboard">
          <TabsList>
            <TabsTrigger value="dashboard">
              Dashboard RH
            </TabsTrigger>
            <TabsTrigger value="performance">
              Indicadores de Desempenho
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard">
            <DashboardRHTab />
          </TabsContent>

          <TabsContent value="performance">
            <PerformanceIndicatorsTab />
          </TabsContent>
        </Tabs>
      </div>
    
  );
}

export default function PeopleManagementScreen() {
  return (
    <ProtectedRoute>
      <PeopleManagementContent />
    </ProtectedRoute>
  );
}